/**
 * Rights-based row filters (`rightsPercontext[<collection>].filters`).
 * Shared because the same filter set has to be applied to the cockpit list (component-handler) and the smart map 
 */

export const LINKED_IN_WORK_ORDERS = 'linkedInWorkOrders';
export const WITHOUT_CATEGORY = 'withoutCategory';
export const CREATED_BY_USER = 'createdByUser';

// work order filters
export const ASSIGNED_TO_USER = 'assignedToUser';
export const NOT_ASSIGNED = 'notAssigned';

export const WORK_ORDER_RIGHTS_FILTERS = [
  CREATED_BY_USER, ASSIGNED_TO_USER, NOT_ASSIGNED,
];

export function hasKnownWorkOrderFilter(filters: string[] | null | undefined): boolean {
  return (filters ?? []).some(f => WORK_ORDER_RIGHTS_FILTERS.includes(f));
}


/**
 * Data-access "filters" on a role's HuntingDistrict row hold raw hunting-district
 * ids - when present, only items
 * whose district id is one of them are kept, and items with no district id at
 * all are dropped too, per spec. 
 */
export function applyHuntingDistrictRightsFilter<T>(
  items: T[],
  allowedHuntingDistrictIds: string[] | null | undefined,
  getHuntingDistrictId: (item: T) => string | null | undefined,
): T[] {
  if (!allowedHuntingDistrictIds?.length) return items;
  const allowed = new Set(allowedHuntingDistrictIds);
  return items.filter(item => {
    const id = getHuntingDistrictId(item);
    return !!id && allowed.has(id);
  });
}

export function huntingDistrictIdsByLinkedId(
  gameRecords: { identifier?: string; huntingDistrictId?: string | null }[] | null | undefined,
  pois: { identifier?: string; huntingDistrictId?: string | null }[] | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  (gameRecords ?? []).forEach(gr => { if (gr.identifier && gr.huntingDistrictId) map.set(gr.identifier, gr.huntingDistrictId); });
  (pois ?? []).forEach(poi => { if (poi.identifier && poi.huntingDistrictId) map.set(poi.identifier, poi.huntingDistrictId); });
  return map;
}

export function workOrderHuntingDistrictId(
  order: { link?: string } | null | undefined,
  hdIdsByLinkedId: Map<string, string>,
): string | undefined {
  const [, id] = (order?.link ?? '').split(':');
  return id ? hdIdsByLinkedId.get(id) : undefined;
}

export function linkedIdsForType(
  orders: { link?: string }[] | null | undefined,
  type: 'geoObject' | 'gameRecord',
): Set<string> {
  const ids = new Set<string>();
  (orders ?? []).forEach(order => {
    const [linkType, id] = (order?.link ?? '').split(':');
    if (linkType === type && id) ids.add(id);
  });
  return ids;
}

export function allowedPoiCategories(filters: string[] | null | undefined): string[] | null {
  if (!filters?.length) return null;
  const categories = filters.filter(f => f !== LINKED_IN_WORK_ORDERS);
  if (!categories.length) return null;
  return categories.filter(f => f !== WITHOUT_CATEGORY);
}

export function applyPoiRightsFilters<T>(
  items: T[],
  filters: string[] | null | undefined,
  getCategory: (item: T) => any,
  getIdentifier: (item: T) => string | undefined,
  linkedIds: Set<string> | null,
): T[] {
  if (!filters?.length) return items;

  let result = items;

  if (filters.includes(LINKED_IN_WORK_ORDERS) && linkedIds) {
    result = result.filter(item => {
      const id = getIdentifier(item);
      return !!id && linkedIds.has(id);
    });
  }

  const categories = filters.filter(f => f !== LINKED_IN_WORK_ORDERS);
  if (!categories.length) return result;

  const matchesCategory = (item: T): boolean => {
    const category = getCategory(item);
    return Array.isArray(category)
      ? category.some((c: string) => categories.includes(c))
      : categories.includes(category);
  };

  if (categories.includes(WITHOUT_CATEGORY)) {
    return categories.length === 1
      ? result.filter(item => !getCategory(item))
      : result.filter(item => !getCategory(item) || matchesCategory(item));
  }

  return result.filter(matchesCategory);
}

export function applyWorkOrderRightsFilters<T>(
  items: T[],
  filters: string[] | null | undefined,
  getCreatedByUserId: (item: T) => string | undefined,
  getTargetUserIds: (item: T) => string[] | undefined,
  selfUserIds: string[],
): T[] {
  const list = items ?? [];
  if (!filters?.length || !selfUserIds?.length) return list;

  const isSelf = (id?: string | null) => !!id && selfUserIds.includes(id);
  const has = (...names: string[]) => names.some(n => filters.includes(n));

  return list.filter(item =>
    (has(CREATED_BY_USER) && isSelf(getCreatedByUserId(item))) ||
    // Assignment lives in targetUserIds, not editorUserId (the last editor).
    (has(ASSIGNED_TO_USER) && (getTargetUserIds(item) ?? []).some(isSelf)) ||
    (has(NOT_ASSIGNED) && !(getTargetUserIds(item)?.length)),
  );
}

export function applyGameRecordRightsFilters<T>(
  items: T[],
  filters: string[] | null | undefined,
  getCreatedByUserId: (item: T) => string | undefined,
  getIdentifier: (item: T) => string | undefined,
  currentUserId: string | null | undefined,
  linkedIds: Set<string> | null,
): T[] {
  if (!filters?.length) return items;

  let result = items;

  if (filters.includes(CREATED_BY_USER) && currentUserId) {
    result = result.filter(item => getCreatedByUserId(item) === currentUserId);
  }

  if (filters.includes(LINKED_IN_WORK_ORDERS) && linkedIds) {
    result = result.filter(item => {
      const id = getIdentifier(item);
      return !!id && linkedIds.has(id);
    });
  }

  return result;
}